import { useState, useEffect, useCallback, useRef } from 'react';
import type { Section } from '../stores/editorStore';

export type SaveStatus = 'idle' | 'pending' | 'saving' | 'saved' | 'error';

export type UseAutoSaveOptions = Readonly<{
  sections: readonly Section[];
  onSave: (sections: readonly Section[]) => Promise<void>;
  debounceMs?: number;
  savedTimeout?: number;
}>;

export const useAutoSave = ({
  sections,
  onSave,
  debounceMs = 2000,
  savedTimeout = 2000,
}: UseAutoSaveOptions) => {
  const [saveStatus, setSaveStatus] = useState<SaveStatus>('idle');
  const debounceTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const savedTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const prevSectionsRef = useRef<readonly Section[]>(sections);

  const performSave = useCallback(async (sectionsToSave: readonly Section[]) => {
    // All sections are content sections now (intro/outro are virtual)
    if (sectionsToSave.length === 0) {
      setSaveStatus('idle');
      return;
    }

    setSaveStatus('saving');

    try {
      await onSave(sectionsToSave);
      setSaveStatus('saved');

      // Reset to idle after timeout
      if (savedTimerRef.current) {
        clearTimeout(savedTimerRef.current);
      }
      savedTimerRef.current = setTimeout(() => {
        setSaveStatus('idle');
      }, savedTimeout);
    } catch (error) {
      setSaveStatus('error');
    }
  }, [onSave, savedTimeout]);

  // Watch for section changes and auto-save
  useEffect(() => {
    // Skip initial render
    if (prevSectionsRef.current === sections) {
      return;
    }

    // Check if sections actually changed
    const hasChanges = JSON.stringify(prevSectionsRef.current) !== JSON.stringify(sections);
    
    if (hasChanges) {
      // Clear existing debounce timer
      if (debounceTimerRef.current) {
        clearTimeout(debounceTimerRef.current);
      }

      // Clear saved timer if running
      if (savedTimerRef.current) {
        clearTimeout(savedTimerRef.current);
        savedTimerRef.current = null;
      }

      setSaveStatus('pending');

      // Set new debounce timer
      debounceTimerRef.current = setTimeout(() => {
        performSave(sections);
        prevSectionsRef.current = sections;
      }, debounceMs);
    }

    return () => {
      if (debounceTimerRef.current) {
        clearTimeout(debounceTimerRef.current);
      }
    };
  }, [sections, debounceMs, performSave]);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      if (debounceTimerRef.current) {
        clearTimeout(debounceTimerRef.current);
      }
      if (savedTimerRef.current) {
        clearTimeout(savedTimerRef.current);
      }
    };
  }, []);

  return {
    saveStatus,
  };
};
